import * as React from 'react'

import {
  Box,
  BoxList,
  Button,
  Icon,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import asyncAction from 'modules/core/lib/asyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import { formatTime } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'

import type Document from 'modules/documents/store/models/Document'
import { useResultConceptTotals } from 'modules/payroll_bundle/hooks/useResultConceptTotals'
import { usePayrollResults } from 'modules/payroll_bundle/lib/PayrollResultContext'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import DeleteModal from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/ResultsPage/_components/_DeleteModal'
import results from 'modules/payroll_bundle/store/collections/results'
import type Result from 'modules/payroll_bundle/store/models/Result'

interface ToolTipProps {
  processedAt: string
  processedBy: string
  document?: Document
  resultStatus: string
}

const TooltipDialog = ({ processedAt, processedBy, document, resultStatus }: ToolTipProps) => {
  const [open, toggle] = useToggle(false)
  const authorUser = document ? users.get(document.attributes.author_id) : undefined
  const authorName = authorUser
    ? getFullDisplayName({
        firstName: authorUser.attributes.first_name,
        lastName: authorUser.attributes.last_name,
      })
    : ''
  const date = moment(document?.attributes.created_at)
  const currentAccess = useCurrentSession().access
  const time = formatTime(currentAccess, date.format('HH:mm'))
  const createdAt = `${date.format('ll')}, ${time}`

  return (
    <Tooltip.ItemList open={open} toggle={toggle} attachment="top center">
      <Icon.Medium icon="info-circle-line" color="grey800" />
      <Box alignItems="flexStart">
        <Text.Normal size="200" capitalize>
          {i18n.t('payroll_bundle.payroll_results.file_item.upload_title').toUpperCase()}
        </Text.Normal>
        <Text.Normal size="200">
          {i18n.t('payroll_bundle.payroll_results.file_item.upload_description', {
            createdAt: document ? createdAt : processedAt,
            authorName: document ? authorName : processedBy,
          })}
        </Text.Normal>
      </Box>
      {resultStatus === i18n.t('payroll_bundle.payroll_results.result_files.processed') && (
        <Box alignItems="flexStart">
          <Text.Normal size="200">
            {i18n.t('payroll_bundle.payroll_results.file_item.process_title').toUpperCase()}
          </Text.Normal>
          <Text.Normal size="200">
            {i18n.t('payroll_bundle.payroll_results.file_item.process_description', {
              processedAt,
              processedBy,
            })}
          </Text.Normal>
        </Box>
      )}
    </Tooltip.ItemList>
  )
}

interface Props {
  result: Result
  resultStatus: string
}

const ProcessedResultItem = ({ result, resultStatus }: Props) => {
  const [open, toggle] = useToggle(false)
  const { currentPeriod, prevPeriod } = usePolicyPeriods()
  const { resultDispatch, uploadedPayrollResults } = usePayrollResults()
  const { refetch: refetchResultConceptTotals, fetching: fetchingResultConceptTotals } =
    useResultConceptTotals(currentPeriod?.id, prevPeriod?.id)

  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false)
  const [isDeleting, setIsDeleting] = React.useState<boolean>(false)
  const [isPending, startTransition] = React.useTransition()
  const createdAt = moment(result.attributes.created_at).format('MMM D, Y, HH:mm')
  const fileName = result.attributes.filename
  const fileAuthor = users.get(result.attributes.author_id)
  const authorName = fileAuthor
    ? getFullDisplayName({
        firstName: fileAuthor.attributes.first_name,
        lastName: fileAuthor.attributes.last_name,
      })
    : undefined
  const document = uploadedPayrollResults.filter(
    (doc) => doc.id === result.attributes.document_id
  )[0]

  const closeModal = () => {
    if (results.toJS().length === 0) {
      resultDispatch({
        type: UPDATE_DATA,
        payload: { showResultsFilesModal: false },
      })
    }

    setShowDeleteModal(false)
  }

  const handleCloseModal = () => {
    closeModal()
  }

  const handleDeleteResultClick = (result: Result) => {
    setIsDeleting(true)
    startTransition(() => {
      deleteResult(result).finally(() => {
        setIsDeleting(false)
      })
    })
  }

  const deleteResult = async (result: Result) => {
    const { success } = await asyncAction(() => result.delete(), { successFlash: false })

    if (success) {
      await Promise.all([
        results.fetch({ data: { policy_period_ids: [currentPeriod?.id, prevPeriod?.id] } }),
        refetchResultConceptTotals(),
      ])
    }

    closeModal()
  }

  return (
    <BoxList>
      <BoxList.Item>
        <Box width="full">
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="spaceBetween"
            paddingBottom="s16"
          >
            <Box
              alignItems="center"
              background={'grey400'}
              borderRadius={{ all: 'abs008' }}
              paddingX="s8"
              paddingY="s2"
            >
              <Text.Normal weight="medium" color="grey1000">
                {resultStatus}
              </Text.Normal>
            </Box>

            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button type="secondary" onClick={toggle(true)} iconName="ellipsis" />
              </PopoverMenu.Trigger>
              {open && (
                <PopoverMenu.Content side="bottom" align="start" disablePortal>
                  <Box flexDirection="column">
                    {document && (
                      <PopoverMenu.HrefOption
                        title="Download file"
                        href={buildUrl(document?.attributes.file)}
                        target="_blank"
                      />
                    )}
                    <PopoverMenu.TouchableOption
                      title={i18n.t('payroll_bundle.payroll_results.file_item.undo_processing')}
                      onClick={() => setShowDeleteModal(true)}
                      danger
                    />
                  </Box>
                </PopoverMenu.Content>
              )}
            </PopoverMenu>
          </Box>

          <Text.Normal size="300" weight="medium" color="grey800" ellipsis>
            {fileName}
          </Text.Normal>
          <Box flexDirection="row" alignItems="center" justifyContent="flexStart">
            <Text.Normal size="200">
              {i18n.t('payroll_bundle.payroll_results.file_item.processed_subtitle', {
                createdAt,
              })}
            </Text.Normal>
            <TooltipDialog
              processedAt={createdAt}
              processedBy={authorName || ''}
              document={document}
              resultStatus={resultStatus}
            />
          </Box>
        </Box>
      </BoxList.Item>
      {showDeleteModal && (
        <DeleteModal
          result={result}
          disabled={fetchingResultConceptTotals || isDeleting}
          onClose={handleCloseModal}
          onDelete={handleDeleteResultClick}
        />
      )}
    </BoxList>
  )
}

export default observer(ProcessedResultItem)
